import Option from '@atlas/uicorn/lib/Control/Option';
import type { TOptionProps, TOptionRef } from '@atlas/uicorn/lib/Control/Option';
import { SIconBase } from '@atlas/uicorn/lib/Icon/IconBase';
import PaintIcon from '@atlas/uicorn/lib/Icon/PaintIcon';
import type { TExample } from '@atlas/uicorn/preview/modules';
import React from 'react';
import styled, { css } from 'styled-components';

const IconButton = React.memo(
    React.forwardRef<TOptionRef, TIconButtonProps>(function IconButton(props, ref): React.ReactElement {
        const { icon, size, loading, ...elementProps } = props;

        return (
            <SIconButton
                {...elementProps}
                flex="center"
                prefix={icon}
                loading={loading && 'prefix'}
                $size={size}
                ref={ref}
            />
        );
    }),
);

export default IconButton;

// #region Styles

export const SIconButton = styled(Option)<{ $size?: number; $danger?: boolean }>`
    --button-icon-transitions: scale 0.1s ease;
    border-radius: ${(p) => p.theme.radius.small};
    transition: color 0.1s ease;
    flex-shrink: 0;

    ${(p) =>
        p.$size
            ? css`
                  min-height: ${p.$size / 10}rem;
                  min-width: ${p.$size / 10}rem;

                  ${SIconBase} {
                      --icon-size: ${p.$size / 10}rem;
                  }
              `
            : ''}

    ${SIconBase} {
        scale: 1;
        transition: var(--button-icon-transitions);
    }

    &:not(:disabled):active {
        ${SIconBase} {
            scale: 0.9;
        }
    }
`;

// #endregion

// #region Types

export type TIconButtonProps = Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'type' | 'prefix' | 'color' | 'children'
> & {
    elementAs?: string;
    icon: React.ReactElement;
    tooltip: NonNullable<TOptionProps['tooltip']>;
    loading?: boolean;
    submit?: boolean;
    danger?: boolean;
    noHoverStyle?: boolean;
    noFocusStyle?: boolean;
    propagate?: boolean;
    size?: number;
    delayClick?: NonNullable<TOptionProps['delayClick']>;
};

export type TIconButtonRef = TOptionRef;

// #endregion

// #region Examples

export const Example: TExample = {
    layout: 'column',
    render(props) {
        const style: React.CSSProperties = {
            display: 'flex',
            gap: '1em',
            width: '100%',
        };

        const tooltip = { placement: 'left', title: 'Paint' } as const;

        return (
            <>
                <p style={style}>
                    <IconButton {...props} tooltip={tooltip} icon={<PaintIcon />} />
                    Default
                </p>
                <p style={style}>
                    <IconButton {...props} tooltip={tooltip} icon={<PaintIcon />} danger />
                    Danger
                </p>
                <p style={style}>
                    <IconButton {...props} tooltip={tooltip} icon={<PaintIcon />} loading />
                    Loading (Implicitly Disabled)
                </p>
                <p style={style}>
                    <IconButton {...props} tooltip={tooltip} icon={<PaintIcon />} loading disabled={false} />
                    Loading & Explicitly Enabled
                </p>
                <p style={style}>
                    <IconButton {...props} tooltip={tooltip} icon={<PaintIcon />} disabled />
                    Disabled
                </p>
            </>
        );
    },
    controls: {
        size: {
            type: 'number',
            initial: 40,
            connected: false,
        },
    },
    handlers: {
        onClick: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('IconButton', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
